import { _isNotBlank } from '@/littledash';
import { arraysEqual } from '@/utils/array.utils';
import { type SampleDefinitionDetailV1, type SampleDefinitionMetadataV1 } from '@/model/SamplePlan.model';
import { type SampleDefinitionTargetV1 } from '@/components/Samples/AssignAnimals/AssignAnimal.util';

/**
 * Type constraint for objects with a name field
 */
type WithName = { name: string };

/**
 * Pattern to match integers and floats (e.g., "1", "1.5", "123.456")
 * Matches: one or more digits, optionally followed by a decimal point and more digits
 */
const numberPattern = /\d+(?:\.\d+)?/g;

/**
 * Extracts all numbers (integers and floats) from a name string.
 * Returns array of all numbers found.
 *
 * Examples:
 *   extractNumbers("Blood 1") → [1]
 *   extractNumbers("Blood 1.1") → [1.1]
 *   extractNumbers("Custom Blood 5.5") → [5.5]
 *   extractNumbers("My 1 Number 333 test") → [1, 333]
 *   extractNumbers("Version 1.2.3") → [1.2, 3]
 *   extractNumbers("Blood") → []
 */
export const extractNumbers = (name: string): Array<number> => {
  if (_isNotBlank(name)) {
    return (name.match(numberPattern) ?? []).reduce<Array<number>>((acc, numberString) => {
      const value = parseFloat(numberString);
      if (Number.isFinite(value)) {
        acc.push(value);
      }
      return acc;
    }, []);
  }
  return [];
};

/**
 * Finds the maximum number used across all items with names.
 * Returns 0 if no numbers found in any names.
 * Works with any object type that has a name field.
 * Supports both integers and floats.
 * Efficiently computes max in O(n) time without intermediate arrays.
 *
 * Examples:
 *   Names: ['Blood 1', 'Custom Blood 5', 'Other Thing 123'] → Returns 123
 *   Names: ['Blood 1.1', 'Blood 1.5', 'Blood 2'] → Returns 2
 *   Names: ['Test 1.9', 'Test 1.95'] → Returns 1.95
 *   Names: ['Blood', 'Sample'] → Returns 0
 */
export const findMaxNumber = <T extends WithName>(existingItems: Array<T>): number => {
  return existingItems.reduce((max, item) => {
    const numbers = extractNumbers(item.name);
    return numbers.reduce((currentMax, num) => Math.max(currentMax, num), max);
  }, 0);
};

/**
 * Generates auto-name for an item.
 * Format: "<prefix> <number>"
 * Number is max across all names in the array + 1.
 * Works with any object type that has a name field.
 * Supports both integers and floats.
 *
 * Examples:
 *   Given ['Blood 1', 'Custom Blood 5', 'My 1 Number 333 test']
 *   Generates: "Blood 334"
 *
 *   Given ['Blood 1.1', 'Blood 1.5']
 *   Generates: "Blood 2.5"
 *
 *   Given ['Test 1.9', 'Test 2.1']
 *   Generates: "Test 3.1"
 */
export const generateName = <T extends WithName>(namePrefix: string, existingItems: Array<T>): string => {
  const maxNumber = findMaxNumber(existingItems);
  return `${namePrefix} ${maxNumber + 1}`;
};

/**
 * Compares two sample definition detail arrays for equality.
 * Details are equal if they have the same length and all corresponding elements
 * have matching type, value, and unit fields.
 */
export const detailsEqual = (a: Array<SampleDefinitionDetailV1>, b: Array<SampleDefinitionDetailV1>): boolean =>
  arraysEqual(a, b, (x, y) => x.type === y.type && x.value === y.value && x.unit === y.unit);

/**
 * Compares two sample definition metadata arrays for equality.
 * Metadata entries are equal if they have the same length and all corresponding
 * elements have matching type and value fields.
 */
export const metadataEqual = (a: Array<SampleDefinitionMetadataV1>, b: Array<SampleDefinitionMetadataV1>): boolean =>
  arraysEqual(a, b, (x, y) => x.type === y.type && x.value === y.value);

type TargetWithArray = Exclude<SampleDefinitionTargetV1, { type: 'no_target' }>;

/**
 * Compares two sample definition targets for equality.
 * Handles the discriminated union type by first checking the target type,
 * then comparing the target arrays if applicable.
 */
export const targetsEqual = (a: SampleDefinitionTargetV1, b: SampleDefinitionTargetV1): boolean => {
  if (a.type !== b.type) {
    return false;
  }
  if (a.type === 'no_target') {
    return true;
  }
  return arraysEqual((a as TargetWithArray).targets, (b as TargetWithArray).targets, (x, y) => x === y);
};
